import { useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { IFilter } from "@components/molecules/filter";
import { useFilterParam } from "@components/molecules/filter";
import type {
  IPageTableParamProps,
  ITableTabProps,
  IUseTableParams,
} from "@components/organisms/pageTable/types";
import type { ITableSearchProps } from "@components/molecules/tableSearch";
import type { ITabObject } from "@components/molecules/headerTabGroup";
import { getParamName } from "@components/molecules/filter/helper";
import { decodeURIComponentSafe } from "@utils";

// ms
const SEARCH_TIME = 800;
const DEFAULT_PAGE_SIZE = 10;

export const useTableParams = ({
  index,
  tabs,
  pageSizeDefault,
}: IUseTableParams) => {
  const { pagination, onPageChange, handleResultsPerPageChange, resetPage } =
    usePagination(index, pageSizeDefault);

  const [filters, setFilters] = useState<IFilter[]>([]);

  const { value, debouncedValue, changeHandler, resetSearch } = useSearchParam(
    index,
    resetPage,
  );
  const { filterProps, filterValues, resetFilter } = useFilterParam(
    filters,
    index,
    resetPage,
  );

  const resetState = useCallback(
    (value: URLSearchParams) => {
      resetPage(value);
      resetFilter();
      resetSearch();
    },
    [resetFilter, resetPage, resetSearch],
  );

  const { selectedTab, onTabChangeHandler } = useTabParam(
    tabs,
    index,
    resetState,
  );

  const searchProps: ITableSearchProps = useMemo(
    () => ({
      value,
      onChange: changeHandler,
    }),
    [changeHandler, value],
  );

  const tabParams: ITableTabProps | undefined = useMemo(
    () =>
      tabs
        ? {
            tabs,
            onChange: onTabChangeHandler,
            selectedKey: selectedTab,
          }
        : undefined,
    [onTabChangeHandler, selectedTab, tabs],
  );

  const tablePropsPagination = useMemo(
    () => ({
      onPageChange,
      page: pagination?.currentPage,
      per_page: pagination?.resultsPerPage,
      onResultPerPageChange: handleResultsPerPageChange,
    }),
    [
      handleResultsPerPageChange,
      onPageChange,
      pagination?.currentPage,
      pagination?.resultsPerPage,
    ],
  );

  const tableProps: IPageTableParamProps = useMemo(
    () => ({
      filterProps,
      searchProps,
      pagination: tablePropsPagination,
      tabProps: tabParams,
    }),
    [filterProps, searchProps, tabParams, tablePropsPagination],
  );

  return useMemo(
    () => ({
      searchValue: debouncedValue,
      paginationValues: pagination,
      tableProps,
      filterValues,
      setFilters,
    }),
    [debouncedValue, filterValues, pagination, tableProps],
  );
};

/**
 * hook that manage pagination
 * @param index index of pagination. If u have more than one pagination in your page, u have to give an index to handle url parameters separately.
 * @param pageSizeDefault
 * @returns
 */
function usePagination(index?: number | string, pageSizeDefault?: number) {
  const navigate = useNavigate();
  const { search, state } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);

  const currentPageParamName = useMemo(
    () => getCurrentPageParamName(index),
    [index],
  );
  const resultPerPagePageParamName = useMemo(
    () => getResultsPerPageParamName(index),
    [index],
  );

  const currentPageParamValue = useMemo(
    () => params.get(currentPageParamName),
    [currentPageParamName, params],
  );
  const resultPerPagePageParamValue = useMemo(
    () => params.get(resultPerPagePageParamName),
    [params, resultPerPagePageParamName],
  );

  const [pagination, setPagination] = useState({
    currentPage:
      currentPageParamValue === null ? 1 : Number(currentPageParamValue),
    resultsPerPage:
      resultPerPagePageParamValue === null
        ? pageSizeDefault || DEFAULT_PAGE_SIZE
        : Number(resultPerPagePageParamValue),
  });

  const setParam = useCallback(
    (name: string, value: string | number, newParams?: URLSearchParams) => {
      params.set(name, String(value));
      newParams?.set(name, String(value));
      navigate(
        { search: newParams ? newParams.toString() : params.toString() },
        { state, replace: true },
      );
    },
    [navigate, params, state],
  );

  const onPageChange = useCallback(
    (page: number) => {
      setPagination((prev) => ({
        ...prev,
        currentPage: page,
      }));
      setParam(currentPageParamName, page);
    },
    [currentPageParamName, setParam],
  );

  const handleResultsPerPageChange = useCallback(
    (resultsPerPage: number) => {
      setPagination({
        currentPage: 1,
        resultsPerPage,
      });
      setParam(resultPerPagePageParamName, resultsPerPage);
      setParam(currentPageParamName, 1);
    },
    [currentPageParamName, resultPerPagePageParamName, setParam],
  );

  const resetPage = useCallback(
    (newParams: URLSearchParams) => {
      setPagination((prev) => ({ ...prev, currentPage: 1 }));
      setParam(currentPageParamName, 1, newParams);
    },
    [currentPageParamName, setParam],
  );

  const resetPageAndSize = useCallback(() => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
      resultsPerPage: pageSizeDefault || DEFAULT_PAGE_SIZE,
    }));
  }, [pageSizeDefault]);

  useEffect(() => {
    if (resultPerPagePageParamValue === null) {
      setPagination((perv) => ({
        ...perv,
        resultsPerPage: pageSizeDefault || DEFAULT_PAGE_SIZE,
      }));
    }
    if (currentPageParamValue === null) {
      setPagination((perv) => ({ ...perv, currentPage: 1 }));
    }
  }, [resultPerPagePageParamValue, currentPageParamValue, pageSizeDefault]);

  return useMemo(
    () => ({
      pagination,
      onPageChange,
      handleResultsPerPageChange,
      resetPage,
      resetPageAndSize,
    }),
    [
      handleResultsPerPageChange,
      onPageChange,
      pagination,
      resetPage,
      resetPageAndSize,
    ],
  );
}

/**
 * user typed something.
 * we set the timer.
 * if the value of the search input has changed before finishing the timeout:
 *   YES => refresh the timeout
 *   NO => we set the new timer
 * in the callback of the timeout we have to clear the timer and set the debounced value.
 *   and if the onChange is not undefined call it.
 * finally return value and onChange to handle the search input value.
 *    and debouncedValue to use in query.
 */
/**
 *
 * @param index
 * @param onChange a function to call when the debounced value has changed.
 */
const useSearchParam = (
  index?: string | number,
  onChange?: (value: URLSearchParams) => void,
) => {
  const navigate = useNavigate();
  const { search, state } = useLocation();
  const searchParamName = useMemo(() => getSearchParamName(index), [index]);
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const searchParamValue = useMemo(
    () => params.get(searchParamName),
    [params, searchParamName],
  );

  // get the search param value from the URL
  const initialValue = useMemo(
    () =>
      searchParamValue === null ? "" : decodeURIComponentSafe(searchParamValue),
    [searchParamValue],
  );

  // state for store the input value and debounced value
  const [value, setValue] = useState(initialValue);
  const [debouncedValue, setDebouncedValue] = useState(initialValue);

  // use ref to store the timer
  // eslint-disable-next-line no-undef
  const timer = useRef<NodeJS.Timeout | null>(null);

  // declare set and delete param to/from the URL
  const setParam = useCallback(
    (value: string) => {
      params.set(searchParamName, value);
      navigate({ search: params.toString() }, { state, replace: true });
    },
    [navigate, params, searchParamName, state],
  );
  const deleteParam = useCallback(() => {
    params.delete(searchParamName);
    navigate({ search: params.toString() }, { state, replace: true });
  }, [navigate, params, searchParamName, state]);

  // timeout callback function
  const setKeyword = useCallback(
    (value: string) => {
      // set the url param
      if (value === "") deleteParam();
      else setParam(value);

      // set the debounced value
      setDebouncedValue(value);
      if (onChange) onChange(params);

      // clear the timer
      if (timer.current !== null) {
        clearTimeout(timer.current);
      }
      timer.current = null;
    },
    [deleteParam, onChange, params, setParam],
  );

  // input change handler
  const changeHandler = useCallback(
    (value: string) => {
      setValue(value);
      const time = value.trim() === "" ? 0 : SEARCH_TIME;

      if (timer.current === null) {
        // set the timeout
        timer.current = setTimeout(() => setKeyword(value.trim()), time);
      } else {
        // refresh the timer
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setKeyword(value.trim()), time);
      }
    },
    [setKeyword],
  );

  useEffect(() => {
    if (searchParamValue === null) {
      setDebouncedValue("");
      setValue("");
    }
  }, [searchParamValue]);

  const resetSearch = useCallback(() => {
    setValue("");
    setParam("");
    setDebouncedValue("");
    setKeyword("");
    deleteParam();
  }, [deleteParam, setKeyword, setParam]);

  return useMemo(
    () => ({ value, debouncedValue, changeHandler, resetSearch }),
    [changeHandler, debouncedValue, resetSearch, value],
  );
};

export const useTabParam = (
  tabs?: ITabObject[],
  index?: string | number,
  onChange?: (value: URLSearchParams) => void,
) => {
  const navigate = useNavigate();
  const { search, state } = useLocation();
  const tabParamName = useMemo(() => getTabParamName(index), [index]);
  const searchParamName = useMemo(() => getSearchParamName(index), [index]);
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const tabParamValue = useMemo(
    () => params.get(tabParamName),
    [params, tabParamName],
  );
  const initialValue = useMemo(
    () => (tabParamValue === null ? tabs?.[0]?.key || "" : tabParamValue),
    [tabParamValue, tabs],
  );

  const [selectedTab, setSelectedTab] =
    useState<ITabObject["key"]>(initialValue);

  // declare set and delete param to/from the URL
  const setParam = useCallback(
    (value: string) => {
      params.set(tabParamName, encodeURIComponent(value));
      if (onChange) onChange(params);
      params.delete(searchParamName);
      navigate({ search: params.toString() }, { state, replace: true });
    },
    [navigate, onChange, params, searchParamName, state, tabParamName],
  );

  useEffect(() => {
    setSelectedTab((tabParamValue || initialValue) as ITabObject["key"]);
  }, [initialValue, tabParamValue]);

  const onTabChangeHandler = useCallback(
    (tabId: ITabObject["key"]) => {
      setSelectedTab(tabId);
      setParam(tabId);
    },
    [setParam],
  );

  return useMemo(
    () => ({ selectedTab, onTabChangeHandler }),
    [onTabChangeHandler, selectedTab],
  );
};

function getTabParamName(index?: string | number) {
  return getParamName("tab", index);
}

function getCurrentPageParamName(index?: string | number) {
  return getParamName("currentPage", index);
}
function getResultsPerPageParamName(index?: string | number) {
  return getParamName("resultsPerPage", index);
}

function getSearchParamName(index?: string | number) {
  return getParamName("keyword", index);
}
